import { useMemo } from "react";
import { useIsValidPalette } from "./useIsValidPalette";
import {
  levels,
  Level,
  Palette,
  UnvalidatedThemeModel,
} from "../types/ThemeModel";

type ColorResolver = (opacity?: number) => string;
type PaletteResolver = Record<Level, ColorResolver>;

export const usePaletteValues = <
  InputPalette extends
    | NonNullable<UnvalidatedThemeModel>["palette"]
    | Array<NonNullable<UnvalidatedThemeModel>["palette"]>
>(
  palette: InputPalette,
  raw = false
): InputPalette extends Array<NonNullable<UnvalidatedThemeModel>["palette"]>
  ? Array<PaletteResolver>
  : PaletteResolver => {
  const isValidPalette = useIsValidPalette(palette);
  if (!isValidPalette) throw new Error(`Invalid palette(s): ${palette}`);

  const resolver = useMemo(() => {
    const bodyStyles = window.getComputedStyle(document.body);
    Array.isArray(palette) ? palette : [palette].map;
    const createPaletteResolver = (palette: Palette) =>
      levels.reduce((map, level) => {
        const propertyKey = level.startsWith("neutral-")
          ? `--palette-current-${level}`
          : `--palette-${palette}-${level}`;
        const value = bodyStyles.getPropertyValue(propertyKey);
        map[level] = (opacity = 1) => {
          if (raw) {
            return value;
          } else {
            return `rgb(${value} / ${opacity})`;
          }
        };

        return map;
      }, {} as PaletteResolver);

    if (Array.isArray(palette)) {
      return palette.map(createPaletteResolver);
    } else return createPaletteResolver(palette);
  }, [palette, raw]);

  // @ts-expect-error This is correct.
  return resolver;
};
